<script lang="ts" setup>
import { ref, provide, watch, watchEffect } from 'vue';
import TabItem from './TabItem.vue';

const props = defineProps<{
	modelValue: string | number
}>();

const emit = defineEmits<{
	"activeTab": [valueOrHash: string | number],
	"update:modelValue": [valueOrHash: string | number]
}>();

const activeTabHash = ref('');
const tabs = ref<{ title: string, hash: string, value: string | number, tabComp: InstanceType<typeof TabItem>}[]>([]);

provide('addTab', (tabTitle: string, tabHash: string, tabValue: string | number, tab: InstanceType<typeof TabItem>) => {
	const count = tabs.value.push({ title: tabTitle, hash: tabHash, value: tabValue, tabComp: tab });

	if (count === 1) {
		activeTabHash.value = tabHash;
	}
});
provide('activeTabHash', activeTabHash);
provide('setActiveTab', (hash: string) => {
	const tab = tabs.value.find(tab => tab.hash === hash);
	if (!tab) {
		alert("Failed to find tab");
		return;
	}
	emit('activeTab', tab.value);
	emit('update:modelValue', tab.value);
});
provide('setTabValue', (hash: string, value: string | number) => {
	const tab = tabs.value.find(tab => tab.hash === hash);
	if (!tab) {
		alert("Failed to find tab");
		return;
	}
	tab.value = value;
});

function setActiveTabByHash(hash: string) {
	activeTabHash.value = hash;
}

watch(() => props.modelValue, (newValue, _oldValue) => {
	const tab = tabs.value.find(tab => tab.value === newValue);
	if (!tab) {
		alert("Failed to find tab");
		return
	}
	setActiveTabByHash(tab.hash);
});

watchEffect(() => {
  	const tab = tabs.value.find(tab => tab.value === props.modelValue);
  	if (tab) {
		activeTabHash.value = tab.hash;
  	}
});

</script>

<style scoped>
.tab-container {
	display: flex;
}

.tab-item {
	padding: 0.5rem;
	cursor: pointer;
	font-weight: 500;
	border-bottom: solid 0.2rem #F4F4F4;
	flex: 1;
}

.tab-selected {
	cursor: pointer;
	color: #146AFF;
	border-bottom: solid 0.2rem #146AFF;
}
</style>

<template>
	<div class="tab-container">
		<div class="tab-item" :class="{
			'tab-selected': tab.hash === activeTabHash
		}" v-for="tab in tabs" :key="tab.title" @click="setActiveTabByHash(tab.hash)">
			<slot name="title">
				{{ tab.title }}
			</slot>
		</div>
	</div>
	<slot />
</template>
